import React, {useContext, useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useParams} from 'react-router-dom';
import {RootState} from '../../store/store';
import {FaUser} from 'react-icons/fa';
import Skeleton from 'react-loading-skeleton';
import {TitleContext} from '../../components/TitleContext';
import DotsMenu from '../../components/DotsMenu';
import ClientSideBar from '../../components/ClientSideBar';
import ClientVisitTable from '../../features/client/clientVisit/ClientVisitTable';
import Drawer from '../../components/Drawer';
import ClientVisitForm from '../../features/client/clientVisit/ClientVisitForm';
import ClientDocumentsTable from '../../features/client/documents/ClientDocumentsTable';
import ClientNotesTable from '../../features/client/notes/ClientNotesTable';
import {ClientThunks} from '../../features/client/clientThunks';
import {AppDispatch} from '../../store/store';
import {setIsEditing} from '../../features/client/clientSlice';
import Modal from '../../components/Modal';
import ClientForm from '../../features/client/ClientForm';
import {formatEthnicity, formatSex} from '../../utils/formatDisplayValues';

const ClientField: React.FC<{
  label: string;
  value?: React.ReactNode;
  loading: boolean;
  width?: number | string;
}> = ({label, value, loading, width = 200}) => (
  <div className="flex flex-col" style={{width}}>
    <div className="text-slate-400 text-xs font-light font-['Inter'] mb-1">
      {label}
    </div>
    <div className="text-slate-800 text-sm font-medium font-['Inter']">
      {loading ? <Skeleton width={width} height={20} /> : value || '-'}
    </div>
  </div>
);


const ClientPage: React.FC = () => {
  const {id} = useParams<{id: string}>();
  const activeTab = useSelector((state: RootState) => state.client.activeTab);
  const client = useSelector((state: RootState) => state.client.client);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const isEditing = useSelector((state: RootState) => state.client.isEditing);
  const openDrawer = () => setIsDrawerOpen(true);
  const closeDrawer = () => setIsDrawerOpen(false);
  const openModal = () => setIsModalOpen(true);
  const closeModal = () => {
    setIsModalOpen(false);
    dispatch(setIsEditing(false));
  };
  const loading = useSelector(
    (state: RootState) => state.client.status === 'loading',
  );
  const {setTitle} = useContext(TitleContext);
  const dispatch = useDispatch<AppDispatch>();

  const handleEdit = () => {
    dispatch(setIsEditing(true));
    openModal();
  };



  // Load client data when component mounts or id changes
  useEffect(() => {
    if (id) {
      dispatch(ClientThunks.show(id));
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (loading && !client) {
      setTitle(
        <div>
          <div className="text-slate-600 text-lg font-medium font-['Inter']  ">
            <Skeleton width={200} height={24} />
          </div>
        </div>,
      );
    } else if (client && client.firstName && client.lastName) {
      setTitle(
        <div>
          <div className="text-slate-600 text-lg font-medium font-['Inter']  ">
            {`${client.firstName} ${client.lastName}`}
          </div>
        </div>,
      );
    } else {
      setTitle(
        <div>
          <div className="text-slate-600 text-lg font-medium font-['Inter']  ">
            Client Details
          </div>
        </div>,
      );
    }
  }, [setTitle, client, loading]);

  if (loading && !client) {
    return (
      <div className="flex flex-row h-full">
        <div className="flex-1 flex flex-col">
          <div className="bg-white border border-slate-200 dark:bg-neutral-800 p-6 rounded-lg flex flex-row items-center gap-8 mb-6 relative">
            <div className="absolute top-6 right-6 z-10">
              <Skeleton width={40} height={40} />
            </div>
            <div className="flex-shrink-0 w-32 h-32 mx-14 bg-slate-100 rounded-full flex justify-center items-center overflow-hidden">
              <Skeleton circle width={128} height={128} />
            </div>
            <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-1">
              <Skeleton width={200} height={40} />
              <Skeleton width={200} height={40} />
              <Skeleton width={200} height={40} />
              <Skeleton width={200} height={40} />
              <Skeleton width={200} height={40} />
              <Skeleton width={200} height={40} />
              <Skeleton width={200} height={40} />
              <Skeleton width={200} height={40} />
              <Skeleton width={200} height={40} />
              <Skeleton width={200} height={40} />
              <Skeleton width={200} height={40} />
              <Skeleton width={200} height={40} />
              <Skeleton width={200} height={40} />
              <Skeleton width={200} height={40} />
              <Skeleton width={200} height={40} />
            </div>
          </div>
          
          {/* Skeleton for tabs content */}
          <div className="p-5 space-y-4 flex flex-col bg-white border border-gray-200 rounded-xl dark:bg-neutral-800 dark:border-neutral-700">
            <div className="text-sm font-medium text-center text-slate-500 border-b border-gray-200 dark:text-gray-400 dark:border-gray-700 overflow-x-auto overflow-y-clip">
              <ul className="flex flex-nowrap -mb-px min-w-max">
                <Skeleton width={60} height={40} className="me-2" />
                <Skeleton width={80} height={40} className="me-2" />
                <Skeleton width={70} height={40} className="me-2" />
              </ul>
            </div>
            <div className="space-y-4">
              <Skeleton height={200} />
              <Skeleton height={100} />
            </div>
          </div>
        </div>
        <div className="w-80">
          <Skeleton height={400} />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-row h-full ">
      <div className="flex-1 flex flex-col">
        <div className="bg-white border border-slate-200 dark:bg-neutral-800 p-6 rounded-lg  flex flex-row items-center gap-8 mb-6 relative">
          <div className="absolute top-6 right-6 z-10">
            <DotsMenu
              options={[
                // { label: 'Assign QMHPs', onClick: () => {} },
                // { label: 'Move to Intake', onClick: () => {} },
                // { label: 'Assign for Re-Assessment', onClick: () => {} },
                // { label: 'Discharge', onClick: () => {} },
                { label: 'Edit', onClick: handleEdit },
              ]}
            />
          </div>
          <div className="flex-shrink-0 w-32 h-32 mx-14 bg-slate-100 rounded-full flex justify-center items-center overflow-hidden">
            {client?.pictureUrl ? (
              <img
                src={client.pictureUrl}
                alt="Profile"
                className="w-full h-full object-cover"
              />
            ) : (
              <FaUser className="text-gray-400 size-16" />
            )}
          </div>
          <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-1">
            <ClientField
              label="First Name"
              value={client?.firstName}
              loading={loading}
              width={200}
            />
            <ClientField
              label="Last Name"
              value={client?.lastName}
              loading={loading}
              width={200}
            />
            <ClientField
              label="Alias Name"
              value={client?.aliasName}
              loading={loading}
              width={200}
            />
            <ClientField
              label="SSN"
              value={client?.ssn}
              loading={loading}
              width={200}
            />
            <ClientField
              label="Legal Guardian"
              value={client?.legalGuardian}
              loading={loading}
              width={200}
            />
            <ClientField
              label="External ID"
              value={client?.externalId}
              loading={loading}
              width={200}
            />
            <ClientField
              label="Sex"
              value={formatSex(client?.sex || '')}
              loading={loading}
              width={200}
            />
            <ClientField
              label="Location"
              value={typeof client?.location === 'string' ? client.location : client?.location?.name}
              loading={loading}
              width={200}
            />
            <ClientField
              label="Ethnicity"
              value={formatEthnicity(client?.ethnicity || '')}
              loading={loading}
              width={200}
            />
            <ClientField
              label="Date of Birth"
              value={client?.dateOfBirth}
              loading={loading}
              width={200}
            />
            <ClientField
              label="Email"
              value={client?.email}
              loading={loading}
              width={200}
            />
            <ClientField
              label="Phone Number"
              value={client?.phoneNumber}
              loading={loading}
              width={200}
            />
            <ClientField
              label="Referral Source"
              value={typeof client?.referralSource === 'string' ? client.referralSource : client?.referralSource?.name}
              loading={loading}
              width={200}
            />
          </div>
        </div>
        
        {activeTab === 'Visits' && (
          <div>
            <ClientVisitTable openDrawer={openDrawer} clientId={id}/>
            <Drawer isOpen={isDrawerOpen} onClose={closeDrawer}>
              <ClientVisitForm closeDrawer={closeDrawer} clientId={id} />
            </Drawer>
          </div>
        )}
        
        {activeTab === 'Notes' && (
          <div>
            <ClientNotesTable openDrawer={openDrawer} clientId={id}/>
          </div>
        )}
        
        {activeTab === 'Documents' && (
          <div>
            <ClientDocumentsTable openDrawer={openDrawer} clientId={id}/>
            <Drawer isOpen={isDrawerOpen} onClose={closeDrawer}>
              <ClientVisitForm closeDrawer={closeDrawer} clientId={id} />
            </Drawer>
          </div>
        )}
      </div>
      <ClientSideBar/>
      <Modal isOpen={isModalOpen} onClose={closeModal} title={`Edit Client: ${client && client.firstName && client.lastName ? `${client.firstName} ${client.lastName}` : 'Loading...'}`}>
        <ClientForm closeModal={closeModal} />
      </Modal>
    </div>
  );
};

export default ClientPage;
